
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Guard, Team } from "../types";
import { getGuardsInUserTeams, getGuardsByTeamId, getUserTeamIds } from "../services/guardFetchService";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useTeams } from "@/pages/applications/hooks/useTeams";
import { UserRole } from "@/pages/users/types";

export const useGuardsData = () => {
  const { toast } = useToast();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole | "all">("all");
  const { roles, isAdmin, isSuperAdmin, isUnassignedAdmin, isManager, hasRole, adminTeamIds, isLoading: rolesLoading } = useUserRoles();
  const { user } = useSupabaseUser();
  const isGuard = hasRole('guard');
  const isClient = hasRole('client');
  
  // Use the centralized useTeams hook that works in teams page
  const { data: teamsData, isLoading: teamsLoading } = useTeams();
  // Cast the teams data to match our Team type
  const teams = teamsData as Team[] || [];
  
  console.log('Teams from useTeams hook:', teams);
  
  // Query to fetch all guards the user has access to
  const { data: allGuards, isLoading: guardsLoading, refetch: refetchGuards } = useQuery({
    queryKey: ['guards', user?.id, isSuperAdmin, isAdmin, isManager, isGuard, isClient, adminTeamIds, selectedRole],
    staleTime: 60_000,
    gcTime: 300_000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    enabled: !!user && !rolesLoading, // wait until roles are resolved to avoid flicker
    queryFn: async () => {
      console.log('Fetching guards...');
      console.log('User roles - isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin, 'isUnassignedAdmin:', isUnassignedAdmin, 'adminTeamIds:', adminTeamIds);
      console.log('Selected role filter:', selectedRole);
      
      try {
        // Super Admins: fetch all guards/managers (exclude admins)
        if (isSuperAdmin && user) {
          console.log('Super Admin user - fetching all guards/managers');
          let query = supabase
            .from('profiles')
            .select('*, trietia, user_roles(role)')
            .neq('status', 'inactive')
            .neq('status', 'archived');

          if (selectedRole !== "all") {
            // Only allow guard and manager roles, never client
            if (selectedRole === 'guard' || selectedRole === 'manager') {
              query = query.eq('Role', selectedRole);
            } else {
              // If selected role is not guard or manager, return empty results
              return [];
            }
          } else {
            query = query.or('Role.eq.guard,Role.eq.manager,Role.is.null');
          }

          const { data: allProfiles, error: allProfilesError } = await query;
          if (allProfilesError) throw allProfilesError;
          
          // Filter out admins, super admins, and clients by checking user_roles table
          if (allProfiles) {
            const profileIds = allProfiles.map(p => p.id);
            const { data: adminRoles } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('user_id', profileIds)
              .in('role', ['admin', 'super_admin', 'client']);
            
            const excludedIds = new Set(adminRoles?.map(r => r.user_id) || []);
            const filteredProfiles = allProfiles.filter(p => 
              !excludedIds.has(p.id) && 
              !p.email?.startsWith('deleted_') && 
              !p.email?.startsWith('archived_')
            );
            console.log('Found profiles (excluding admins, clients, and deleted users):', filteredProfiles);
            return filteredProfiles as Guard[];
          }
          
          return [];
        }

        // Team Admins: guards in their assigned teams + guards they created
        if (isAdmin && !isSuperAdmin && adminTeamIds.length > 0 && user) {
          console.log('Team Admin user - fetching guards in assigned teams + created by admin:', adminTeamIds);
          let allTeamGuards: Guard[] = [];
          
          // 1. Fetch guards from assigned teams
          for (const teamId of adminTeamIds) {
            const teamGuards = await getGuardsByTeamId(teamId);
            allTeamGuards = [...allTeamGuards, ...teamGuards];
          }
          
          // 2. Fetch guards created by this admin
          let createdQuery = supabase
            .from('profiles')
            .select('*, trietia, user_roles(role)')
            .eq('created_by', user.id)
            .neq('status', 'inactive')
            .neq('status', 'archived');

          if (selectedRole !== "all") {
            if (selectedRole === 'guard' || selectedRole === 'manager') {
              createdQuery = createdQuery.eq('Role', selectedRole);
            }
          } else {
            createdQuery = createdQuery.or('Role.eq.guard,Role.eq.manager,Role.is.null');
          }

          const { data: createdGuards } = await createdQuery;
          if (createdGuards) {
            allTeamGuards = [...allTeamGuards, ...createdGuards];
          }
          
          // Remove duplicates
          const uniqueGuards = allTeamGuards.filter((guard, index, self) => 
            index === self.findIndex((g) => g.id === guard.id)
          );
          
          // Filter out admins, super admins, clients, and deleted users
          const profileIds = uniqueGuards.map(p => p.id);
          if (profileIds.length > 0) {
            const { data: excludedRoles } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('user_id', profileIds)
              .in('role', ['admin', 'super_admin', 'client']);
            
            const excludedIds = new Set(excludedRoles?.map(r => r.user_id) || []);
            const nonExcludedGuards = uniqueGuards.filter(p => 
              !excludedIds.has(p.id) &&
              !p.email?.startsWith('deleted_') &&
              !p.email?.startsWith('archived_')
            );
            
            // Only show guards and managers
            const validGuards = nonExcludedGuards.filter(g => g.Role === 'guard' || g.Role === 'manager' || !g.Role);
            console.log('Team admin guards (from teams + created):', validGuards);
            return selectedRole !== 'all' ? validGuards.filter(g => g.Role === selectedRole) : validGuards;
          }
          
          return [];
        }

        // Unassigned admins: see guards they created
        if (isUnassignedAdmin && user) {
          console.log('Unassigned admin - fetching guards created by this admin');
          
          // Unassigned admins only see what THEY directly created (strict isolation)
          const effectiveAdminId = user.id;
          
          let query = supabase
            .from('profiles')
            .select('*, trietia, user_roles(role)')
            .eq('created_by', effectiveAdminId)
            .neq('status', 'inactive')
            .neq('status', 'archived');

          if (selectedRole !== "all") {
            if (selectedRole === 'guard' || selectedRole === 'manager') {
              query = query.eq('Role', selectedRole);
            } else {
              return [];
            }
          } else {
            query = query.or('Role.eq.guard,Role.eq.manager,Role.is.null');
          }

          const { data: createdProfiles, error: createdProfilesError } = await query;
          if (createdProfilesError) throw createdProfilesError;
          
          // Filter out admins, super admins, and clients
          if (createdProfiles) {
            const profileIds = createdProfiles.map(p => p.id);
            const { data: excludedRoles } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('user_id', profileIds)
              .in('role', ['admin', 'super_admin', 'client']);
            
            const excludedIds = new Set(excludedRoles?.map(r => r.user_id) || []);
            const filteredProfiles = createdProfiles.filter(p => !excludedIds.has(p.id));
            console.log('Unassigned admin guards:', filteredProfiles);
            return filteredProfiles as Guard[];
          }
          
          return [];
        }

        // Managers (non-admin): only guards in their assigned teams
        if (isManager && !isAdmin && user) {
          console.log('Manager user - fetching only guards in assigned teams');
          const { data: managerTeams, error: managerTeamsError } = await supabase
            .from('manager_teams')
            .select('team_id')
            .eq('manager_id', user.id);
          if (managerTeamsError) throw managerTeamsError;

          const managerTeamIds = managerTeams?.map(mt => mt.team_id) || [];
          if (managerTeamIds.length === 0) return [];

          let allTeamGuards: Guard[] = [];
          for (const teamId of managerTeamIds) {
            const teamGuards = await getGuardsByTeamId(teamId);
            allTeamGuards = [...allTeamGuards, ...teamGuards];
          }
          const uniqueGuards = allTeamGuards.filter((guard, index, self) => index === self.findIndex((g) => g.id === guard.id));
          // Only show guards and managers, never clients
          const validGuards = uniqueGuards.filter(g => g.Role === 'guard' || g.Role === 'manager' || !g.Role);
          return selectedRole !== 'all' ? validGuards.filter(g => g.Role === selectedRole) : validGuards;
        }
        
        // Guards/Clients: only guards in their own teams
        if ((isGuard || isClient) && !isAdmin && !isManager && user) {
          console.log('Guard/Client user - fetching only guards in their teams');
          console.log('isClient:', isClient, 'selectedRole:', selectedRole);
          const userTeamIds = await getUserTeamIds(user.id);
          if (userTeamIds.length === 0) return [];

          let allTeamGuards: Guard[] = [];
          for (const teamId of userTeamIds) {
            const teamGuards = await getGuardsByTeamId(teamId);
            allTeamGuards = [...allTeamGuards, ...teamGuards];
          }
          const uniqueGuards = allTeamGuards.filter((guard, index, self) => index === self.findIndex((g) => g.id === guard.id));
          console.log('Before client filtering:', uniqueGuards.map(g => `${g.first_name} ${g.last_name} - ${g.Role}`));
          
          // For clients: only show guards, never managers or other roles
          // For others: show guards and managers, never clients
          const validGuards = isClient && !isAdmin 
            ? uniqueGuards.filter(g => g.Role === 'guard') 
            : uniqueGuards.filter(g => g.Role === 'guard' || g.Role === 'manager' || !g.Role);
          
          console.log('After client filtering:', validGuards.map(g => `${g.first_name} ${g.last_name} - ${g.Role}`));
          const finalResult = selectedRole !== 'all' ? validGuards.filter(g => g.Role === selectedRole) : validGuards;
          console.log('Final result:', finalResult.map(g => `${g.first_name} ${g.last_name} - ${g.Role}`));
          return finalResult;
        }

        // Fallback (roles resolved but not admin/manager/guard/client): team-based access
        if (user) {
          console.log('Fallback - fetching guards in user teams');
          const fallbackGuards = await getGuardsInUserTeams();
          // For clients: only show guards, never managers or other roles
          // For others: show guards and managers, never clients
          const validGuards = isClient && !isAdmin 
            ? fallbackGuards.filter(g => g.Role === 'guard') 
            : fallbackGuards.filter(g => g.Role === 'guard' || g.Role === 'manager' || !g.Role);
          return selectedRole !== 'all' ? validGuards.filter(g => g.Role === selectedRole) : validGuards;
        }
        
        return [];
      } catch (error: any) {
        console.error("Error fetching guards:", error);
        toast({
          title: "Error loading guards",
          description: error?.message || 'Unknown error',
          variant: "destructive",
        });
        return [];
      }
    },
  });

  // Query to get guards in a specific team when a team filter is selected
  const { data: filteredGuards, isLoading: filteredGuardsLoading } = useQuery({
    queryKey: ['guards-by-team', selectedTeamId, selectedRole],
    staleTime: 60_000,
    gcTime: 300_000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      if (!selectedTeamId) return [];
      
      console.log(`Fetching guards for team: ${selectedTeamId}`);
      console.log('User roles in team filter - isClient:', isClient, 'isAdmin:', isAdmin);
      let teamGuards = await getGuardsByTeamId(selectedTeamId);
      
      console.log('Team guards before filtering:', teamGuards.map(g => `${g.first_name} ${g.last_name} - ${g.Role}`));
      
      // For clients: only show guards, never managers or other roles
      // For others: show guards and managers, never clients
      teamGuards = isClient && !isAdmin 
        ? teamGuards.filter(guard => guard.Role === 'guard') 
        : teamGuards.filter(guard => guard.Role === 'guard' || guard.Role === 'manager' || !guard.Role);
      
      console.log('Team guards after client filtering:', teamGuards.map(g => `${g.first_name} ${g.last_name} - ${g.Role}`));
      
      // Apply role filter to team guards
      if (selectedRole !== "all") {
        teamGuards = teamGuards.filter(guard => guard.Role === selectedRole);
      }
      
      console.log(`Found ${teamGuards.length} guards in selected team with role filter`);
      console.log('Final team guards result:', teamGuards.map(g => `${g.first_name} ${g.last_name} - ${g.Role}`));
      return teamGuards;
    },
    enabled: !!selectedTeamId && !!user && !rolesLoading, // Only run when a team is selected and roles are ready
  });

  // Determine which guards to display based on team filter
  const guards = selectedTeamId ? filteredGuards : allGuards;
  const isLoading = rolesLoading || (guardsLoading || teamsLoading) || 
                    (selectedTeamId ? filteredGuardsLoading : false);

  // Query for archived guards
  const { data: archivedGuards } = useQuery({
    queryKey: ['archived-guards', user?.id],
    staleTime: 60_000,
    queryFn: async () => {
      if (!user || !isAdmin) return [];
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*, trietia')
        .eq('status', 'archived')
        .or('Role.eq.guard,Role.eq.manager,Role.is.null');
      
      if (error) throw error;
      return data as Guard[];
    },
    enabled: !!user && !!isAdmin && !rolesLoading
  });

  const handleTeamFilterChange = (teamId: string | null) => {
    console.log('Team filter changed to:', teamId);
    setSelectedTeamId(teamId);
  };

  const handleRoleFilterChange = (role: UserRole | "all") => {
    console.log('Role filter changed to:', role);
    setSelectedRole(role);
  };

  return {
    guards,
    archivedGuards,
    teams,
    isLoading,
    isAdmin: isAdmin || isManager, // Managers should have admin-like capabilities in guards page
    isClient,
    refetchGuards,
    handleTeamFilterChange,
    handleRoleFilterChange,
    selectedRole
  };
};
